import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/brand";

export default function SetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/auth/token/${token}`)
      .then((r) => { if (mounted) setTokenInfo(r.data); })
      .catch((e) => { if (mounted) setError(e.response?.data?.detail || "Link inválido ou expirado"); });
    return () => { mounted = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("A senha precisa ter pelo menos 6 caracteres."); return; }
    if (pw1 !== pw2)    { toast.error("As senhas não coincidem.");                       return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw1 });
      setUser(data);
      toast.success(tokenInfo?.purpose === "invite" ? "Bem-vindo(a)!" : "Senha alterada com sucesso");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]">
      <div className="w-full max-w-md text-center">
        <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-12 w-auto mb-8 mx-auto" />
        <div className="card" data-testid="set-password-error-card">
          <i className="fa-solid fa-link-slash text-4xl text-red-500 mb-4"></i>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-3">Link inválido</h1>
          <p className="text-sm text-slate-600 mb-6">{error}. Solicite um novo link de redefinição.</p>
          <Link to="/recuperar-senha" className="btn btn-primary w-full justify-center" data-testid="set-password-request-new">
            <i className="fa-solid fa-paper-plane"></i> Solicitar novo link
          </Link>
        </div>
      </div>
    </div>;
  }
  if (!tokenInfo) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">
      <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Validando link…
    </div>;
  }

  const isInvite = tokenInfo.purpose === "invite";

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]">
      <div className="w-full max-w-md">
        <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-12 w-auto mb-8" />
        <form onSubmit={submit} className="card" data-testid="set-password-form">
          <div className="overline mb-2">{isInvite ? "Bem-vindo(a) à Sollute" : "Redefinição de senha"}</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
            {isInvite ? `Olá, ${tokenInfo.name || tokenInfo.email}` : "Crie sua nova senha"}
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            {isInvite
              ? "Para concluir seu acesso, defina uma senha. Use pelo menos 6 caracteres."
              : "Escolha uma nova senha de pelo menos 6 caracteres."}
            <br/><span className="font-mono text-xs text-slate-500">{tokenInfo.email}</span>
          </p>

          <label className="label">Senha</label>
          <input type="password" required minLength={6} value={pw1} onChange={(e) => setPw1(e.target.value)}
                 className="input mb-4" autoComplete="new-password" data-testid="set-password-pw1" />
          <label className="label">Confirme a senha</label>
          <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)}
                 className="input mb-6" autoComplete="new-password" data-testid="set-password-pw2" />

          <button type="submit" disabled={busy} className="btn btn-primary w-full justify-center" data-testid="set-password-submit">
            {busy ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-check"></i>}
            {isInvite ? "Ativar acesso" : "Salvar nova senha"}
          </button>
        </form>
      </div>
    </div>
  );
}
